/**
 * useWidgetReducer Hook
 *
 * Custom hook that wraps useReducer with convenient action creators.
 * Provides a clean API for managing RoseWidget UI state.
 */

import { useCallback, useMemo, useReducer } from 'react';
import {
    getInitialState,
    type WidgetAction,
    type WidgetInitialStateOptions,
    type WidgetState,
    widgetReducer,
} from '../../utils/widget/widgetReducer';

interface WidgetActions {
    expand: (source?: string) => void;
    collapse: () => void;
    minimize: () => void;
    setViewLayout: (viewLayout: WidgetState['viewLayout']) => void;
    sendMessageStart: () => void;
    sendMessageSuccess: () => void;
    sendMessageError: (error: string) => void;
    trackImpression: () => void;
    trackHover: () => void;
    hiddenZoneClicked: () => void;
    reset: () => void;
    switchLayout: () => void;
}

interface UseWidgetReducerReturn {
    state: WidgetState;
    actions: WidgetActions;
    getState: () => 'collapsed' | 'expanded' | 'minimized';
    dispatch: React.Dispatch<WidgetAction>;
}

/**
 * Hook for managing widget UI state with useReducer
 *
 * @returns Widget state, action creators, and helper functions
 *
 * @example
 * const { state, actions, getState } = useWidgetReducer();
 *
 * // Use action creators
 * actions.expand('cta_button');
 * actions.sendMessageStart();
 *
 * // Access state
 * if (state.isLoading) { ... }
 */
export function useWidgetReducer(
    initialStateOptions: WidgetInitialStateOptions = {}
): UseWidgetReducerReturn {
    const [state, dispatch] = useReducer(widgetReducer, getInitialState(initialStateOptions));

    // Action creators - memoized for stable references
    const actions = useMemo<WidgetActions>(
        () => ({
            expand: (source?: string) => dispatch({ type: 'EXPAND', source }),
            collapse: () => dispatch({ type: 'COLLAPSE' }),
            minimize: () => dispatch({ type: 'MINIMIZE' }),
            setViewLayout: (viewLayout: WidgetState['viewLayout']) =>
                dispatch({ type: 'SET_VIEW_LAYOUT', payload: viewLayout }),
            sendMessageStart: () => dispatch({ type: 'SEND_MESSAGE_START' }),
            sendMessageSuccess: () => dispatch({ type: 'SEND_MESSAGE_SUCCESS' }),
            sendMessageError: (error: string) =>
                dispatch({ type: 'SEND_MESSAGE_ERROR', payload: error }),
            trackImpression: () => dispatch({ type: 'TRACK_IMPRESSION' }),
            trackHover: () => dispatch({ type: 'TRACK_HOVER' }),
            hiddenZoneClicked: () => dispatch({ type: 'HIDDEN_ZONE_CLICKED' }),
            reset: () => dispatch({ type: 'RESET' }),
            switchLayout: () => dispatch({ type: 'SWITCH_LAYOUT' }),
        }),
        []
    );

    // Computed widget state helper
    const getState = useCallback((): 'collapsed' | 'expanded' | 'minimized' => {
        if (state.isExplicitlyMinimized) return 'minimized';
        if (state.isExpanded) return 'expanded';
        return 'collapsed';
    }, [state.isExpanded, state.isExplicitlyMinimized]);

    return {
        state,
        actions,
        getState,
        dispatch, // Expose dispatch for custom actions if needed
    };
}
